<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Detalhes da validação</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Acompanhe os números validados e visualize quais possuem WhatsApp.
          </p>
        </div>

        <div class="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
            @click="voltarParaValidacoes"
          >
            <svg
              class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
            Voltar
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div
          class="rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-sm md:col-span-2"
        >
          <p class="text-sm font-medium text-gray-500">Campanha</p>
          <div class="mt-2 truncate text-lg font-semibold text-gray-900">
            {{ campanha && campanha.nome ? campanha.nome : "—" }}
          </div>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
          <p class="text-sm font-medium text-gray-500">Login</p>
          <div class="mt-2 truncate text-lg font-semibold text-gray-900">
            {{ loginCampanha }}
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <UiCard
          v-for="card in cards"
          :key="card.title"
          class="transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">
                {{ card.title }}
              </p>

              <h2 class="mt-2 text-3xl font-semibold text-gray-900">
                {{ card.value }}
              </h2>
            </div>

            <div
              class="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100"
            >
              <component
                :is="getCardIconComponent(card.icon)"
                :class="['h-5 w-5', card.color]"
              />
            </div>
          </div>
        </UiCard>
      </div>

      <div
        class="w-full overflow-hidden sm:rounded-lg border border-gray-200 bg-white shadow-sm"
      >
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">IDX</th>
                <th class="px-6 py-4 text-left font-medium">Telefone</th>
                <th class="px-6 py-4 text-center font-medium">Status</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(item, idx) in lista"
                :key="item._id || idx"
                class="border-b border-gray-100 transition hover:bg-slate-50"
              >
                <td class="px-6 py-5 font-medium text-gray-700">
                  {{ (paginaAtual - 1) * itensPorPagina + idx + 1 }}
                </td>

                <td class="px-6 py-5 text-gray-700">
                  {{ item.telefone }}
                </td>

                <td class="px-6 py-5 text-center">
                  <UiBadge :type="getStatusType(item)">
                    {{ traduzStatus(item) }}
                  </UiBadge>
                </td>
              </tr>

              <tr v-if="!lista || lista.length === 0">
                <td colspan="3" class="px-6 py-12 text-center text-sm text-gray-400">
                  Nenhum registro encontrado.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        :current-page="paginaAtual"
        :total-pages="totalPaginas"
        :total-items="total"
        :page-size="itensPorPagina"
        item-label="registros"
        @paginate="setPage"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import AppPagination from "@/components/AppPagination.vue";
import UiCard from "@/components/ui/UiCard.vue";
import UiBadge from "@/components/ui/UiBadge.vue";
import {
  HeroArrowRightOnRectangle,
  HeroCheckCircle,
  HeroXCircle,
  HeroBars3,
  HeroUser,
} from "@/components/icons/heroicons";

export default {
  components: {
    AppPagination,
    UiCard,
    UiBadge,
    HeroArrowRightOnRectangle,
    HeroCheckCircle,
    HeroXCircle,
    HeroBars3,
    HeroUser,
  },

  data() {
    return {
      skip: 0,
      total: 0,
      route: "campaigns",
      campanha: null,
      moment: moment,
      lista: [],
      paginaAtual: 1,
      itensPorPagina: 25,
    };
  },

  computed: {
    loginCampanha() {
      const login = this.campanha && this.campanha.login ? this.campanha.login : null;

      if (!login) return "—";

      const nome = login.nome || "";
      const email = login.email || "";

      if (nome && email) return `${nome} - ${email}`;
      if (nome) return nome;
      if (email) return email;

      return "—";
    },

    cards() {
      return [
        {
          title: "Total",
          value: this.formatNumber(
            this.campanha && this.campanha.total ? this.campanha.total : this.total
          ),
          icon: "menu",
          color: "text-blue-500",
        },
        {
          title: "Tem WhatsApp",
          value: this.formatNumber(
            this.campanha && this.campanha.totalEnviado ? this.campanha.totalEnviado : 0
          ),
          icon: "check",
          color: "text-green-500",
        },
        {
          title: "Não tem WhatsApp",
          value: this.formatNumber(
            this.campanha && this.campanha.totalFalhas ? this.campanha.totalFalhas : 0
          ),
          icon: "error",
          color: "text-red-500",
        },
        {
          title: "Exibidos",
          value: this.formatNumber(this.lista ? this.lista.length : 0),
          icon: "send",
          color: "text-indigo-500",
        },
      ];
    },

    totalPaginas() {
      return Math.max(1, Math.ceil(this.total / this.itensPorPagina));
    },
  },

  methods: {
    getCardIconComponent(iconName) {
      switch (iconName) {
        case "menu":
          return "HeroBars3";
        case "check":
          return "HeroCheckCircle";
        case "error":
          return "HeroXCircle";
        case "send":
          return "HeroArrowRightOnRectangle";
        case "user":
          return "HeroUser";
        default:
          return "HeroBars3";
      }
    },

    formatNumber(value) {
      return new Intl.NumberFormat("pt-BR").format(Number(value || 0));
    },

    traduzStatus(item) {
      if (!item) return "Em fila";

      if (item.result && item.temzap && item.enviou) {
        return "Tem zap";
      }

      if (item.result && (!item.temzap || !item.enviou)) {
        return "Não tem zap";
      }

      return "Em fila";
    },

    getStatusType(item) {
      const status = this.traduzStatus(item);

      if (status === "Tem zap") return "success";
      if (status === "Não tem zap") return "danger";

      return "default";
    },

    voltarParaValidacoes() {
      this.$router.push({ path: "/temzap" });
    },

    async setPage(page) {
      this.paginaAtual = page;
      this.skip = (page - 1) * this.itensPorPagina;
      window.scrollTo(0, 0);
      await this.start();
    },

    async start() {
      const id = this.$route.params.id
        ? this.$route.params.id.match(/^[0-9a-fA-F]{24}$/)[0]
        : "";

      if (id) {
        const req = await this.$http.get(
          `/v1/campanhasLZV2/${id}/contatos?limit=${this.itensPorPagina}&skip=${this.skip}`
        );

        this.campanha = req.data.campanha;
        this.lista = req.data.data || [];
        this.total = req.data.total || 0;
      }
    },
  },

  beforeMount() {
    this.start();
  },
};
</script>

<style scoped></style>
